import { action, makeObservable, observable } from 'mobx';

import { WidgetEntity } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';

import type { HeatmapWidgetState } from '@go-widgets/well-logs-widget/dist/HeatmapWidget/store/HeatmapWidgetState';
import type { HeatmapWidgetStateParams } from '@go-widgets/well-logs-widget/dist/HeatmapWidget/types';
import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';

export type THeatmapSerializedInternalState = {
  stateParams: Partial<HeatmapWidgetStateParams>;
  wellId: number | null;
};

type HeatmapWidgetOptions = {
  type: string;
  stateParams: Partial<HeatmapWidgetStateParams>;
  wellId: number | null;
};

export class HeatmapWidgetEntity extends WidgetEntity {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: { w: 500, h: 250 },
    maxInPixels: null,
    min: null,
    max: { w: 256, h: 100 },
  };

  @observable state: HeatmapWidgetState | null = null;
  @observable type: string;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<HeatmapWidgetStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;

  constructor(
    { type, stateParams, wellId }: HeatmapWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly i18 = requireService('i18')
  ) {
    super(widgetOptions);

    this.type = type;
    this.wellId = wellId;
    this.stateParams = stateParams;

    makeObservable(this);
  }

  get title(): string {
    return this.i18.t('widgets:Heatmap.title', { defaultValue: 'Heatmap' });
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setStateParams(stateParams: Partial<HeatmapWidgetStateParams>): void {
    this.stateParams = stateParams;
  }

  @action.bound
  setState(state: HeatmapWidgetState | null): void {
    this.state = state;
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
    this.state?.setAppParams({
      active: this.inActiveTab,
    });
  }

  get isControlWidget(): boolean {
    return false;
  }

  getNameT(): string {
    return 'widgets:Heatmap.title';
  }

  @action.bound
  setFullscreen(value: boolean): void {
    this.isFullscreen = value;
  }

  @action.bound
  setWellboreName(wellboreName: string): void {
    this.wellboreName = wellboreName;
  }

  override destroy() {
    this.state?.destroy();
  }
}
